import React, { useState, useMemo } from 'react';
import { Company, PolicyType, SystemStats } from '../../types';
import { StatsRibbon } from './StatsRibbon';
import { SearchFilterBar } from './SearchFilterBar';
import { CompanyGrid } from './CompanyGrid';
import { CompanyList } from './CompanyList';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface CompanyCatalogProps {
  companies: Company[];
  loading: boolean;
  error: string | null;
  stats: SystemStats;
  onSelectCompany: (companyId: string, policyType: PolicyType) => void;
  onJobCreated?: (job: any) => void;
}

export const CompanyCatalog: React.FC<CompanyCatalogProps> = ({
  companies,
  loading,
  error,
  stats,
  onSelectCompany,
  onJobCreated,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'revisions'>('updated');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    companies.forEach((c) => {
      if (c.category) cats.add(c.category);
    });
    return Array.from(cats);
  }, [companies]);

  // Filter & Sort Companies
  const filteredAndSortedCompanies = useMemo(() => {
    let result = [...companies];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          c.domain.toLowerCase().includes(q) ||
          (c.description && c.description.toLowerCase().includes(q))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter((c) => c.category === selectedCategory);
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'revisions') {
        const revA = a.totalRevisions || a.total_revisions || a.revisionCounts?.total || 0;
        const revB = b.totalRevisions || b.total_revisions || b.revisionCounts?.total || 0;
        return revB - revA;
      }
      // Default: 'updated'
      const timeA = new Date(a.updatedAt || a.updated_at || a.lastScrapedAt || 0).getTime();
      const timeB = new Date(b.updatedAt || b.updated_at || b.lastScrapedAt || 0).getTime();
      return timeB - timeA;
    });

    return result;
  }, [companies, searchQuery, selectedCategory, sortBy]);

  return (
    <div className="space-y-6">
      {/* Live System Stats Banner */}
      <StatsRibbon stats={stats} />

      {/* Filter and Search Bar */}
      <SearchFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        categories={categories}
      />

      {/* Main Content Area */}
      {loading && companies.length === 0 ? (
        <LoadingSpinner message="Loading monitored companies..." />
      ) : viewMode === 'grid' ? (
        <CompanyGrid
          companies={filteredAndSortedCompanies}
          onSelectCompany={onSelectCompany}
          onJobCreated={onJobCreated}
        />
      ) : (
        <CompanyList
          companies={filteredAndSortedCompanies}
          onSelectCompany={onSelectCompany}
          onJobCreated={onJobCreated}
        />
      )}
    </div>
  );
};
